import React, { useState, useEffect } from 'react';
import { QrReader } from 'react-qr-reader';
import { deliveryApi } from '../utils/deliveryApi';
import { formatCurrency } from '../utils/helpers';

const DRIVER_ID_KEY = 'flotto_driver_id';
const DRIVER_NAME_KEY = 'flotto_driver_name';

function FlottoDriver() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [driverId, setDriverId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [scannerActive, setScannerActive] = useState(false);
  const [claimedOrders, setClaimedOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    // Check if already logged in
    const savedDriverId = localStorage.getItem(DRIVER_ID_KEY);
    const savedDriverName = localStorage.getItem(DRIVER_NAME_KEY);
    
    if (savedDriverId && savedDriverName) {
      setDriverId(savedDriverId);
      setDriverName(savedDriverName);
      setIsLoggedIn(true);
      loadDriverOrders(savedDriverId);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      const result = await deliveryApi.driverLogin({
        driver_id: driverId,
        password: password
      });
      
      if (result.success) {
        localStorage.setItem(DRIVER_ID_KEY, driverId);
        localStorage.setItem(DRIVER_NAME_KEY, result.driver.name);
        setDriverName(result.driver.name);
        setIsLoggedIn(true);
        setSuccess(`Willkommen, ${result.driver.name}!`);
        loadDriverOrders(driverId);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.message || 'Login fehlgeschlagen');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(DRIVER_ID_KEY);
    localStorage.removeItem(DRIVER_NAME_KEY);
    setIsLoggedIn(false);
    setDriverId('');
    setDriverName('');
    setPassword('');
    setClaimedOrders([]);
  };

  const loadDriverOrders = async (id) => {
    setLoadingOrders(true);
    try {
      const orders = await deliveryApi.getDriverOrders(id);
      setClaimedOrders(orders || []);
    } catch (err) {
      console.error('Error loading orders:', err);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleScan = async (result, error) => {
    if (result) {
      const token = result.text;
      setScannerActive(false);
      
      try {
        setError(null);
        const order = await deliveryApi.driverScan({
          driver_id: driverId,
          token: token
        });
        
        setSuccess(`Lieferung erfolgreich übernommen! (Order: ${order.customer_name})`);
        setTimeout(() => setSuccess(null), 5000);
        
        // Reload orders
        loadDriverOrders(driverId);
      } catch (err) {
        setError(err.message || 'QR-Code konnte nicht gescannt werden');
      }
    }
    
    if (error) {
      console.error('QR Scan Error:', error);
    }
  };

  const handleNavigate = (order) => {
    const address = order.delivery_address.formatted_address;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const handleCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await deliveryApi.updateOrderStatus(orderId, {
        status: newStatus,
        actor: driverId
      });
      setSuccess(`Status geändert zu: ${newStatus}`);
      setTimeout(() => setSuccess(null), 3000);
      loadDriverOrders(driverId);
    } catch (err) {
      setError(err.message || 'Statuswechsel fehlgeschlagen');
    }
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center p-4">
        <div className="dark-card rounded-lg shadow-2xl p-8 w-full max-w-md" data-testid="driver-login-form">
          <div className="text-center mb-6">
            <h1 className="text-4xl font-bold text-purple-600 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              flotto.app
            </h1>
            <p className="text-[var(--text-secondary)]">Fahrer-Login</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-[var(--error-bg)] text-[var(--error-text)] rounded-lg border border-red-200">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fahrer-ID
              </label>
              <input
                type="text"
                value={driverId}
                onChange={(e) => setDriverId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="fahrer-001"
                required
                data-testid="driver-id-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="••••••••"
                required
                data-testid="driver-password-input"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-purple-600 text-white py-3 rounded-lg font-bold hover:bg-purple-700 transition-colors"
              data-testid="driver-login-button"
            >
              Anmelden
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[var(--text-secondary)]">
            Demo: fahrer-001 / demo123
          </div>
        </div>
      </div>
    );
  }

  // Main Driver Dashboard
  return (
    <div className="min-h-screen bg-[var(--bg-tertiary)]">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 shadow-lg">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
              flotto.app
            </h1>
            <p className="text-sm text-purple-100">Hallo, {driverName}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-4 py-2 dark-card text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors"
            data-testid="driver-logout-button"
          >
            Abmelden
          </button>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div className="max-w-4xl mx-auto mt-4 px-4">
          <div className="p-4 bg-[var(--error-bg)] text-[var(--error-text)] rounded-lg border border-red-200">
            {error}
          </div>
        </div>
      )}

      {success && (
        <div className="max-w-4xl mx-auto mt-4 px-4">
          <div className="p-4 bg-[var(--success-bg)] text-[var(--success-text)] rounded-lg border border-green-200">
            {success}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {/* QR Scanner Button */}
        <div className="dark-card rounded-lg shadow border p-6">
          <h2 className="text-xl font-bold mb-4">Lieferung übernehmen</h2>
          
          {!scannerActive ? (
            <button
              onClick={() => setScannerActive(true)}
              className="w-full py-4 bg-purple-600 text-white rounded-lg font-bold text-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
              data-testid="open-qr-scanner-button"
            >
              <span>📷</span>
              <span>QR-Code scannen</span>
            </button>
          ) : (
            <div>
              <div className="mb-4">
                <QrReader
                  onResult={handleScan}
                  constraints={{ facingMode: 'environment' }}
                  containerStyle={{ width: '100%' }}
                  data-testid="qr-scanner"
                />
              </div>
              <button
                onClick={() => setScannerActive(false)}
                className="w-full py-3 bg-[var(--bg-tertiary)]0 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                data-testid="close-qr-scanner-button"
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>

        {/* Active Orders */}
        <div className="dark-card rounded-lg shadow border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Meine Lieferungen</h2>
            <button
              onClick={() => loadDriverOrders(driverId)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium text-sm"
              data-testid="refresh-driver-orders-button"
            >
              🔄 Aktualisieren
            </button>
          </div>

          {loadingOrders ? (
            <div className="text-center py-8 text-[var(--text-secondary)]">Lade Lieferungen...</div>
          ) : claimedOrders.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-secondary)]">
              Noch keine aktiven Lieferungen
            </div>
          ) : (
            <div className="space-y-4" data-testid="driver-orders-list">
              {claimedOrders.map((order) => (
                <div
                  key={order.id}
                  className="border border-purple-200 rounded-lg p-4 bg-purple-50"
                  data-testid={`driver-order-${order.id}`}
                >
                  {/* Customer Info */}
                  <div className="mb-3">
                    <h3 className="text-lg font-bold text-purple-900">{order.customer_name}</h3>
                    <div className="text-sm text-gray-700 space-y-1 mt-2">
                      <div className="flex items-center space-x-2">
                        <span>📍</span>
                        <span>{order.delivery_address.formatted_address}</span>
                      </div>
                      {order.notes && (
                        <div className="flex items-start space-x-2 bg-yellow-100 p-2 rounded mt-2">
                          <span>📝</span>
                          <span className="font-medium text-yellow-900">{order.notes}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="mb-3 p-3 dark-card rounded-lg">
                    <div className="text-xs font-medium text-[var(--text-secondary)] mb-2">Artikel:</div>
                    <div className="space-y-1">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="text-sm flex justify-between">
                          <span>{item.qty}x {item.name}</span>
                          <span className="font-medium">
                            {formatCurrency(item.qty * item.price_net * (1 + item.vat_rate / 100))}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t mt-2 pt-2 flex justify-between font-bold">
                      <span>Gesamt:</span>
                      <span className="text-green-600">{formatCurrency(order.total_gross)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => handleCall(order.customer_phone)}
                      className="py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-colors flex items-center justify-center space-x-2"
                      data-testid={`call-customer-${order.id}`}
                    >
                      <span>📞</span>
                      <span>Anrufen</span>
                    </button>
                    <button
                      onClick={() => handleNavigate(order)}
                      className="py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2"
                      data-testid={`navigate-${order.id}`}
                    >
                      <span>🗺️</span>
                      <span>Navigation</span>
                    </button>
                  </div>

                  {/* Delivery Status Button */}
                  {order.status === 'dispatched' && (
                    <button
                      onClick={() => handleStatusUpdate(order.id, 'delivered')}
                      className="w-full mt-2 py-3 bg-purple-600 text-white rounded-lg font-bold hover:bg-purple-700 transition-colors"
                      data-testid={`mark-delivered-${order.id}`}
                    >
                      ✓ Als zugestellt markieren
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FlottoDriver;
